import {
  ChangeEvent,
  DragEvent,
  MouseEvent,
  ReactNode,
  useCallback,
  useState,
} from 'react';
import { Control, FieldValues, Path, useController } from 'react-hook-form';
import styled, { css } from 'styled-components';

import { ReactComponent as ImgIcon } from '@/assets/icons/img-placeholder.svg';
import { SrOnly } from '@/components/Layout/Parts';
import ProgressBar from '@/components/ProgressBar';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { MediaResource } from '@/types/resources';
import { uploadFileToS3 } from '@/utils/s3';

interface DropzoneProps {
  $dragging: boolean;
}

const Dropzone = styled.div<DropzoneProps>`
  border-radius: 4px;
  border: 1px dashed ${({ theme }) => theme.colors.lightGray};
  color: ${({ theme }) => theme.colors.gray};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  ${({ theme, $dragging }) =>
    $dragging &&
    css`
      border-color: ${theme.colors.purpleLight};
    `}
`;

export const Title = styled(Text)`
  margin: 10px 0 6px;
`;

export const Notice = styled(Text)`
  opacity: 0.5;
  text-align: center;
`;

const Preview = styled.div`
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  display: flex;
  align-items: center;
  padding: 16px;
  position: relative;
`;

const Img = styled.img`
  max-width: 128px;
  height: auto;
  margin-right: 16px;
  object-fit: cover;
`;

const FileInfo = styled.div`
  flex: 1;
  text-align: left;
  padding-left: 16px;
  overflow-x: hidden;
`;

const FileName = styled(Text)`
  margin-bottom: 8px;
  text-overflow: ellipsis;
  /* overflow-x: hidden; */
`;

const ClearButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  transition: 0.2s ease-out opacity;
  &:hover {
    opacity: 0.75;
  }
`;

const InnerLabel = styled(Text)`
  cursor: pointer;
`;

const StyledProgressBar = styled(ProgressBar)`
  height: 8px;
`;

const validImageTypes = [
  'image/png',
  'image/gif',
  'image/jpg',
  'image/jpeg',
  'image/svg+xml',
];

interface Props<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  existingMedia?: MediaResource;
  accept?: string;
  notes?: ReactNode;
  className?: string;
  maxFileSizeInMB?: number;
  id: string;
  disabled?: boolean;
}

const ImageField = <T extends FieldValues>({
  name,
  control,
  accept = 'image/*',
  notes = <>JPG, PNG, GIF up to 2MB</>,
  maxFileSizeInMB = 2,
  className,
  existingMedia,
  disabled = false,
  id,
}: Props<T>) => {
  const alerts = useAlerts();

  const [file, setFile] = useState<File | null>(null);
  const [media, setMedia] = useState<MediaResource | null>(
    existingMedia ?? null,
  );

  const [dragging, setDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProggress, setUploadProgress] = useState(0);

  const {
    field: { onChange },
  } = useController({
    name,
    control,
  });

  const ensureFileIsValid = useCallback(
    (file: File) => {
      if (!validImageTypes.includes(file.type)) {
        return false;
      }

      if (file.size > maxFileSizeInMB * 1024 * 1024) {
        return false;
      }

      return true;
    },
    [maxFileSizeInMB],
  );

  const uploadImage = useCallback(
    async (file: File) => {
      try {
        if (!ensureFileIsValid(file)) {
          return alerts.error('Invalid file!', file.name);
        }

        setUploadProgress(0);
        setIsUploading(true);

        const path = await uploadFileToS3(
          file,
          (progressEvent: ProgressEvent) => {
            setUploadProgress(
              Math.round((progressEvent.loaded / progressEvent.total) * 100),
            );
          },
        );

        setFile(file);
        onChange(path);
        setIsUploading(false);
      } catch (_) {
        alerts.error();
        setUploadProgress(0);
        setIsUploading(false);
      }
    },
    [alerts, ensureFileIsValid, onChange],
  );

  const onInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files?.length) {
        return;
      }

      uploadImage(e.target.files[0]);
    },
    [uploadImage],
  );

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const handleDragEnter = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer?.items?.length) {
      setDragging(true);
    }
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);

    if (!e.dataTransfer?.files?.length) {
      return;
    }

    uploadImage(e.dataTransfer.files[0]);
    e.dataTransfer.clearData();
  };

  const onClearImage = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    onChange('');
    setFile(null);
    setMedia(null);
  };

  const previewable = file ?? media;

  const src = media
    ? media.versions.original
    : file
      ? URL.createObjectURL(file)
      : '';

  return (
    <div className={className}>
      {isUploading ? (
        <StyledProgressBar
          progress={uploadProggress}
          color="primary"
          showPercentage
        />
      ) : (
        <>
          {previewable ? (
            <Preview>
              {!disabled && (
                <ClearButton onClick={onClearImage}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 4L4 12"
                      stroke="#222222"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M12 12L4 4"
                      stroke="#222222"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </ClearButton>
              )}
              <Img src={src} alt="" />
              <FileInfo>
                <FileName as="div" $type="smallMedium">
                  {previewable.name}
                </FileName>
                <Text as="div" $type="smallMedium" $color="gray">
                  {Math.floor(previewable.size / 1024)}Kb
                </Text>
              </FileInfo>
            </Preview>
          ) : (
            <Dropzone
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              $dragging={dragging}
            >
              <ImgIcon />
              <Title $type="smallBold" $color="gray" as="div">
                Drop image here or{' '}
                <InnerLabel
                  as="label"
                  $type="smallBold"
                  $color="purple"
                  htmlFor={id}
                >
                  browse
                  <SrOnly
                    as="input"
                    type="file"
                    accept={accept}
                    id={id}
                    onChange={onInputChange}
                  />
                </InnerLabel>
              </Title>
              <Notice $type="extraSmall" $color="gray" as="div">
                {notes}
              </Notice>
            </Dropzone>
          )}
        </>
      )}
    </div>
  );
};

export default ImageField;
